/**
 * GrcNav — bandeau de navigation GRC unifié et condensé (juin 2026).
 *
 * Remplace les 11 onglets précédents (trop chargés) par 6 entrées :
 *   Tableau de bord · Communication · Planning · Suivi des cautions & locations
 *   · Dossiers (menu déroulant : Scolaires / Associations / Subventions)
 *   · Pointage factures
 *
 * « Communication » = ancienne « Inbox complète » (qui regroupe désormais la
 * messagerie interne, les alertes municipales et les newsletters, accessibles
 * via les boutons rapides du dashboard et la page Communication elle-même).
 *
 * Composant partagé pour éviter la divergence entre pages GRC.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';

export type GrcNavKey =
  | 'dashboard' | 'communication' | 'infos' | 'referendums' | 'dechets' | 'planning' | 'presence' | 'locations' | 'dossiers' | 'factures' | 'cahier'
  | 'deliberations' | 'reglages';

function Tab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 text-xs font-medium text-grc-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

export function GrcNav({ active }: { active: GrcNavKey }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // « Pointage factures » est réservé aux gestionnaires (route /grc/factures
  // exclut le rôle agent) : on le masque pour l'agent, comme dans les autres
  // briques (on n'affiche que les entrées réellement accessibles).
  const estGestionnaire = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  // Groupe « Suivi quotidien » : le pointage factures n'y figure que pour un
  // gestionnaire.
  // Planning UNIFIÉ (20/07) : « Planning de travail » et « Présence » fusionnés
  // en une seule entrée « Planning » — présence et travail cohabitent dans la
  // page via un sélecteur de sous-vue.
  // Refonte UX, chantier 3 (maquette 2, validée le 24/09) — 5 groupes + Réglages :
  // Équipe · Communication · Accueil et services · Dossiers · Conseil municipal.
  // La messagerie interne quitte le GRC : elle est dans « Messages » (en-tête).
  // Toutes les adresses restent les mêmes : on a regroupé le menu, pas les pages.
  const accueilItems: MenuItem[] = [
    { to: '/grc/locations', label: 'Locations et cautions' },
    ...(estGestionnaire ? [{ to: '/grc/factures', label: 'Pointage factures' }] : []),
    { to: '/grc/services', label: 'Services et vie quotidienne' },
    { to: '/grc/etat-civil', label: 'État civil' },
    { to: '/grc/cahier', label: 'Cahier de l’accueil' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tab active={active === 'dashboard'} onClick={() => navigate('/grc')}>Tableau de bord</Tab>
      <Tab active={active === 'planning' || active === 'presence'} onClick={() => navigate('/grc/planning')}>Équipe</Tab>
      <MenuDeroulant
        label="Communication"
        active={active === 'communication' || active === 'infos' || active === 'referendums'}
        items={[
          { to: '/grc/messages', label: 'Boîte citoyenne' },
          { to: '/grc/alertes', label: 'Alertes municipales' },
          { to: '/grc/alertes/modeles', label: 'Modèles d\'alerte' },
          { to: '/grc/newsletters', label: 'Newsletter' },
          { to: '/grc/infos-pratiques', label: 'Infos pratiques' },
          { to: '/grc/referendums', label: 'Référendums' },
        ]}
        onNavigate={navigate}
      />
      <MenuDeroulant
        label="Accueil et services"
        active={active === 'locations' || active === 'factures' || active === 'cahier' || active === 'dechets'}
        items={accueilItems}
        onNavigate={navigate}
      />
      <MenuDeroulant
        label="Dossiers"
        active={active === 'dossiers'}
        items={[
          { to: '/grc/dossiers-scolaires', label: 'Dossiers scolaires' },
          { to: '/grc/dossiers-associations', label: 'Dossiers associations' },
          { to: '/grc/subventions', label: 'Subventions' },
          { to: '/grc/dossiers-petite-enfance', label: 'Dossiers petite enfance' },
          { to: '/grc/dossiers-dechetterie', label: 'Dossiers déchetterie' },
        ]}
        onNavigate={navigate}
      />
      <Tab active={active === 'deliberations'} onClick={() => navigate('/grc/deliberations')}>Conseil municipal</Tab>
      {estGestionnaire && <Tab active={active === 'reglages'} onClick={() => navigate('/grc/reglages')}>Réglages</Tab>}
    </div>
  );
}

type MenuItem = { to: string; label: string };

/**
 * MenuDeroulant — bouton de groupe qui ouvre un menu en position fixe (échappe
 * aux overflow / backdrop-blur / sticky des conteneurs parents). Se ferme au
 * clic extérieur, au scroll et au resize. Le bouton se surligne quand un de ses
 * items est actif (repère de position).
 */
function MenuDeroulant({
  label, active, items, onNavigate,
}: {
  label: string;
  active: boolean;
  items: MenuItem[];
  onNavigate: (to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function toggle() {
    setOpen((o) => {
      const next = !o;
      if (next && btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 4, left: r.left });
      }
      return next;
    });
  }

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(t) && btnRef.current && !btnRef.current.contains(t)) setOpen(false);
    }
    function onScrollResize() { setOpen(false); }
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          active
            ? 'inline-flex h-9 items-center gap-1 rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 text-xs font-medium text-grc-fg backdrop-blur-glass'
            : 'inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg'
        }
      >
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="min-w-[210px] rounded-r-md border border-border bg-surface shadow-r-lg p-1"
        >
          {items.map((it) => (
            <button
              key={it.to}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); onNavigate(it.to); }}
              className="block w-full rounded-r-sm px-3 py-2 text-left text-xs text-ink hover:bg-grc-tint/40"
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
